import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updateUserProfile, getUserEventRegistrations, getCurrentMembership } from '../services/api';
import Alert from '../components/common/Alert';
import { COUNTRIES } from '../constants/countries';

function Profile({ user, setUser }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    profile: {
      bio: '',
      job_title: '',
      company: '',
      website: '',
      receive_emails: true
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [registrations, setRegistrations] = useState([]);
  const [membership, setMembership] = useState(null);
  const [membershipLoading, setMembershipLoading] = useState(true);
  
  // Load user data when component mounts
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        postal_code: user.postal_code || '',
        country: user.country || '',
        profile: {
          bio: user.profile?.bio || '',
          job_title: user.profile?.job_title || '',
          company: user.profile?.company || '',
          website: user.profile?.website || '',
          receive_emails: user.profile?.receive_emails !== false
        }
      });
    }
    const loadRegs = async () => {
      try {
        const res = await getUserEventRegistrations();
        const all = res.results || res;
        // /event-registrations/ returns every registration in the system
        // for staff/admins, not just the caller's own - filter to the
        // current user or an admin would see everyone else's registrations
        // listed on their own profile.
        setRegistrations(all.filter(reg => reg.user === user?.id));
      } catch (e) {
        // non-fatal
      }
    };
    loadRegs();
  }, [user]);

  // Load membership data
  useEffect(() => {
    const loadMembership = async () => {
      try {
        const membershipData = await getCurrentMembership();
        setMembership(membershipData);
      } catch (error) {
        // No active membership is not an error
        setMembership(null);
      } finally {
        setMembershipLoading(false);
      }
    };

    loadMembership();
  }, []);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('profile.')) {
      const profileField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          [profileField]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const updatedUser = await updateUserProfile(formData);
      setUser(updatedUser);
      setSuccess('Profile updated successfully!');
      
      // Scroll to top to show success message
      window.scrollTo(0, 0);
    } catch (error) {
      setError(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-6">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Your Profile</h1>
        <p className="text-gray-600 mt-2">
          Update your personal information and preferences.
        </p>
      </header>
      
      {error && (
        <Alert type="error" message={error} onClose={() => setError('')} />
      )}

      {success && (
        <Alert type="success" message={success} onClose={() => setSuccess('')} />
      )}
      
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <form onSubmit={handleSubmit}>
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  name="first_name"
                  id="first_name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  name="last_name"
                  id="last_name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  id="phone"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm"
                  value={user?.email || ''}
                  disabled
                />
                <p className="text-xs text-gray-500 mt-1">Email address cannot be changed.</p>
              </div>
            </div>
          </div>
          
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Address</h2>
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                  Street Address
                </label>
                <input
                  type="text"
                  name="address"
                  id="address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={formData.address}
                  onChange={handleChange}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    id="city"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.city}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                    State / Province
                  </label>
                  <input
                    type="text"
                    name="state"
                    id="state"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.state}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label htmlFor="postal_code" className="block text-sm font-medium text-gray-700 mb-1">
                    ZIP / Postal Code
                  </label>
                  <input
                    type="text"
                    name="postal_code"
                    id="postal_code"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.postal_code}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <select
                    name="country"
                    id="country"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.country}
                    onChange={handleChange}
                  >
                    <option value="">Select a country</option>
                    {COUNTRIES.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          
          {/* Membership Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Membership</h2>
            {membershipLoading ? (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="animate-pulse flex space-x-4">
                  <div className="rounded-full bg-gray-200 h-10 w-10"></div>
                  <div className="flex-1 space-y-2 py-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ) : membership ? (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium mr-3">
                      Active
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{membership.membership_type.name}</h3>
                      {(membership.membership_type.price > 0 && membership.membership_type.price !== '0' && membership.membership_type.price !== 0.00) && membership.current_period_end && membership.current_period_end !== null && (
                        <p className="text-sm text-gray-600">
                          Expires {new Date(membership.current_period_end + 'T00:00:00').toLocaleDateString()}
                          {membership.days_until_expiry !== null && (
                            <span> ({membership.days_until_expiry} days remaining)</span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                  {membership.membership_type.price > 0 && (
                    <div className="text-right">
                      <div className="font-bold text-blue-600">
                        ${membership.membership_type.price}/{membership.membership_type.duration_months === 3 ? 'quarter' : 'year'}
                      </div>
                    </div>
                  )}
                </div>

                {/* Benefits Preview */}
                {membership.membership_type.benefits?.features?.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-800 mb-2">Your Benefits:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {membership.membership_type.benefits.features.slice(0, 6).map((benefit, index) => (
                        <div key={index} className="flex items-start text-sm">
                          <svg className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700">{benefit}</span>
                        </div>
                      ))}
                    </div>
                    {membership.membership_type.benefits.features.length > 6 && (
                      <p className="text-sm text-gray-500 mt-2">
                        +{membership.membership_type.benefits.features.length - 6} more benefits
                      </p>
                    )}
                  </div>
                )}

                <div className="flex space-x-3">
                  {/* Upgrade for Free Plans */}
                  {(membership.membership_type.price == 0 || membership.membership_type.price === '0' || membership.membership_type.price === 0.00) && (
                    <Link
                      to="/membership"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                      Upgrade to Pro
                    </Link>
                  )}
                  
                  {/* Renewal Warning for paid plans */}
                  {(membership.membership_type.price > 0 && membership.membership_type.price !== '0' && membership.membership_type.price !== 0.00) && membership.days_until_expiry !== null && membership.days_until_expiry < 30 && (
                    <Link
                      to="/membership"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
                    >
                      <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Renew Now
                    </Link>
                  )}
                  
                  {/* Manage Plan - Only for paid plans */}
                  {(membership.membership_type.price > 0 && membership.membership_type.price !== '0' && membership.membership_type.price !== 0.00) && (
                    <Link
                      to="/membership"
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Manage Plan
                    </Link>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="text-center">
                  <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Membership</h3>
                  <p className="text-gray-600 mb-4">
                    Join our community to access exclusive content, events, and member benefits.
                  </p>
                  <Link
                    to="/membership"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Choose Your Plan
                  </Link>
                </div>
              </div>
            )}
          </div>
          
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Professional Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="profile.job_title" className="block text-sm font-medium text-gray-700 mb-1">
                  Job Title
                </label>
                <input
                  type="text"
                  name="profile.job_title"
                  id="profile.job_title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={formData.profile.job_title}
                  onChange={handleChange}
                />
              </div>
              
              <div>
                <label htmlFor="profile.company" className="block text-sm font-medium text-gray-700 mb-1">
                  Company / Organization
                </label>
                <input
                  type="text"
                  name="profile.company"
                  id="profile.company"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={formData.profile.company}
                  onChange={handleChange}
                />
              </div>
              
              <div className="md:col-span-2">
                <label htmlFor="profile.website" className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  name="profile.website"
                  id="profile.website"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={formData.profile.website}
                  onChange={handleChange}
                  placeholder="https://yourwebsite.com"
                />
              </div>
              
              <div className="md:col-span-2">
                <label htmlFor="profile.bio" className="block text-sm font-medium text-gray-700 mb-1">
                  Bio
                </label>
                <textarea
                  name="profile.bio"
                  id="profile.bio"
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={formData.profile.bio}
                  onChange={handleChange}
                  placeholder="Tell us about yourself..."
                ></textarea>
              </div>
            </div>
          </div>
          
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Preferences</h2>
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="profile.receive_emails"
                  name="profile.receive_emails"
                  type="checkbox"
                  className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                  checked={formData.profile.receive_emails}
                  onChange={handleChange}
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="profile.receive_emails" className="font-medium text-gray-700">
                  Receive email notifications
                </label>
                <p className="text-gray-500">Get updates about events, membership, and organization news.</p>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end">
            <button
              type="submit"
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">Your Event Registrations</h2>
        {registrations.length === 0 ? (
          <p className="text-gray-600">No registrations yet.</p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {registrations.map(reg => (
              <li key={reg.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">{reg.event_name}</div>
                  <div className="text-sm text-gray-600">Status: {reg.status_display || reg.status}</div>
                </div>
                <div className="text-sm text-gray-700">{reg.price_paid ? `$${reg.price_paid}` : 'Free'}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default Profile;
